import React, { useState } from 'react'
import { Col, Row, Collapse } from 'reactstrap'
import { BI_AUT003Context } from '../context'
import { fnGetValueByKey } from '../../../helpers/functions'

const colorMap = {
  'color-branco2': '#F5F5F5',
  'color-branco3': '#F0F0F0',
  'color-cinza1': '#E2E2E2',
  'color-cinza2': '#D9D9D9',
  'color-cinza3': '#A8A8A8',
  'color-cinza4': '#6C6C6C',
  'color-cinza5': '#353535',
  'color-azul1': '#0F1D42',
  'color-azul2': '#263874',
  'color-azul3': '#244EA2',
  'color-azul4': '#498EF2',
  'color-azul5': '#82C0F9',
  'color-azul410': '#C1E0FF',
  'color-vermelho1': '#EC221F',
  'color-vermelho2': '#BA0501',
}

const resolveColor = (value, fallback) => {
  if (!value) return fallback
  if (typeof value === 'string' && value.startsWith('#')) return value
  return colorMap[value] || fallback
}

export default function GridBody() {
  const { gridComponents } = React.useContext(BI_AUT003Context)
  const [selectedStepIndex, setSelectedStepIndex] = React.useState(0)
  const [hoveredIndex, setHoveredIndex] = useState(null)
  const [openAccordions, setOpenAccordions] = useState()

  const mapStatus = s => {
    const v = String(s || '').toUpperCase()
    if (v === 'EXECUTADO') return 'completed'
    if (v === 'DESABILITADO') return 'disabled'
    return 'pending'
  }

  const parseEventos = (conteudo) => {
    if (!Array.isArray(conteudo)) return {}
    const eventosPorEtapa = {}
    conteudo.forEach(evento => {
      const etapaId = evento.id_etapa
      if (!eventosPorEtapa[etapaId]) {
        eventosPorEtapa[etapaId] = {}
      }
      let agenda = null
      if (evento.evento_agenda) {
        try {
          agenda = typeof evento.evento_agenda === 'string'
            ? JSON.parse(evento.evento_agenda)
            : evento.evento_agenda
        } catch (e) {
          console.warn('Erro ao parsear evento_agenda:', e)
        }
      }

      const ocorrenciaId = evento.id_ocorrencia || 1
      if (!eventosPorEtapa[etapaId][ocorrenciaId]) {
        eventosPorEtapa[etapaId][ocorrenciaId] = []
      }
      eventosPorEtapa[etapaId][ocorrenciaId].push({
        title: evento.evento_descricao || '',
        date: evento.evento_data || '',
        value: evento.evento_valor || '',
        user: evento.usuario || '',
        link: evento.redirect_link || '',
        valueIcon: evento.evento_icone || '',
        valorIcone: evento.evento_valor_icone || '',
        hasLink: !!evento.redirect_link,
        ctaText: evento.botao_texto || '',
        redirectIcon: evento.redirect_icone || '',
        redirectTarget: evento.redirect_target || '_blank',
        botaoTarget: evento.botao_target || '_blank',
        botaoLink: evento.botao_link || '',
        eventoTexto: evento.evento_texto || '',
        corFundo: evento.evento_cor_fundo || '',
        corIcone: evento.evento_cor_icone || '',
        agenda
      })
    })
    return eventosPorEtapa
  }

  const timelineSteps = React.useMemo(() => {
    try {
      const body = gridComponents?.BODY?.[0]
      if (!body?.Childrens) return []

      const timelineCompChildrens = fnGetValueByKey(body.Childrens, 'TIMELINE', false, 'Childrens')
      if (!timelineCompChildrens) {
        return []
      }

      const etapasValor = fnGetValueByKey(timelineCompChildrens, 'PROCEDURE_RESULTSET_ETAPAS')
      if (!etapasValor) {
        return []
      }

      const conteudo = typeof etapasValor === 'string'
        ? JSON.parse(etapasValor)
        : etapasValor

      if (!Array.isArray(conteudo)) return []

      return conteudo.map(step => ({
        id_etapa: step.id_etapa,
        icon: step.icone || 'fas fa-circle',
        status: mapStatus(step.status),
        label: step.descricao || `Etapa ${step.id_etapa || ''}`,
        corBorda: step.etapa_cor_borda || '',
        corFundo: step.etapa_cor_fundo || '',
        corIcone: step.etapa_cor_icone || '',
        setFocus: String(step.setFocus || '').toUpperCase() === 'TRUE'
      }))
    } catch (e) {
      console.error('Erro ao processar etapas da timeline:', e)
      return []
    }
  }, [gridComponents])

  React.useEffect(() => {
    const focusIndex = timelineSteps.findIndex(step => step.setFocus)
    if (focusIndex >= 0) {
      setSelectedStepIndex(focusIndex)
    }
  }, [timelineSteps])

  const timelineEvents = React.useMemo(() => {
    try {
      const body = gridComponents?.BODY?.[0]
      if (!body?.Childrens) return {}

      const timelineCompChildrens = fnGetValueByKey(body.Childrens, 'TIMELINE', false, 'Childrens')
      if (!timelineCompChildrens) {
        return {}
      }

      const eventosValor = fnGetValueByKey(timelineCompChildrens, 'PROCEDURE_RESULTSET_EVENTOS')
      if (!eventosValor) {
        return {}
      }

      const conteudo = typeof eventosValor === 'string'
        ? JSON.parse(eventosValor)
        : eventosValor

      return parseEventos(conteudo)
    } catch (e) {
      console.error('Erro ao processar eventos da timeline:', e)
      return {}
    }
  }, [gridComponents])

  const HorizontalTimeline = () => {
    const [hoveredIndex, setHoveredIndex] = useState(null)

    const StepCircle = ({ icon, status, overlay, label, index, corBorda, corFundo, corIcone }) => {
      const size = 65
      const statusLower = String(status).toLowerCase()
      const isCompleted = statusLower === 'completed'
      const isDisabled = statusLower === 'disabled'
      const inner = 44

      const isHovered = hoveredIndex === index

      const defaultColors = isCompleted
        ? { borda: '#498EF2', fundo: '#498EF2', icone: '#FFFFFF' }
        : isDisabled
          ? { borda: '#A8A8A8', fundo: '#FFFFFF', icone: '#A8A8A8' }
          : { borda: '#498EF2', fundo: '#FFFFFF', icone: '#A8A8A8' }

      const colorBorda = resolveColor(corBorda, defaultColors.borda)
      const colorFundo = resolveColor(corFundo, defaultColors.fundo)
      const colorIcone = resolveColor(corIcone, defaultColors.icone)

      const hasCustomColors = !!(corBorda || corFundo || corIcone)
      const useSolid = hasCustomColors ? colorBorda === colorFundo : isCompleted

      const circleOpacity = 1
      return (
        <div
          className="position-relative"
          style={{
            width: size,
            height: size,
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            zIndex: hoveredIndex === index ? 10 : 5
          }}
          onMouseEnter={() => setHoveredIndex(index)}
          onMouseLeave={() => setHoveredIndex(null)}
          onClick={() => { if (!isDisabled) { setSelectedStepIndex(index) } }}
        >
          {useSolid ? (
            <>
              <div className="rounded-circle"
                   style={{
                     width: size,
                     height: size,
                     backgroundColor: colorFundo,
                     opacity: circleOpacity,
                     animation: isHovered ? 'gradualGrow 150ms ease-in-out forwards' : 'none',
                     transition: !isHovered ? 'transform 150ms ease-in-out' : 'none',
                     transform: !isHovered ? 'scale(1)' : 'none',
                     transformOrigin: 'center center'
                   }}></div>
              <div className="position-absolute top-50 start-50 translate-middle d-flex align-items-center justify-content-center"
                   style={{ transform: 'translate(-50%, -50%)', transformOrigin: 'center center' }}>
                <i className={icon} style={{ fontSize: 28, color: colorIcone }}></i>
              </div>
            </>
          ) : (
            <>
              <div className="rounded-circle" style={{
                width: size,
                height: size,
                border: `9px solid ${colorBorda}`,
                backgroundColor: colorFundo,
                opacity: circleOpacity,
                animation: isHovered ? 'gradualGrow 150ms ease-in-out forwards' : 'none',
                transition: !isHovered ? 'transform 150ms ease-in-out' : 'none',
                transform: !isHovered ? 'scale(1)' : 'none',
                transformOrigin: 'center center'
              }}></div>
              <div className="position-absolute top-50 start-50 translate-middle rounded-circle d-flex align-items-center justify-content-center"
                   style={{ width: inner, height: inner, backgroundColor: colorFundo, opacity: circleOpacity, transform: 'translate(-50%, -50%)', transformOrigin: 'center center' }}>
                <i className={icon} style={{ fontSize: 28, color: colorIcone }}></i>
              </div>
            </>
          )}

          {hoveredIndex === index && (
            <div className=" position-absolute start-50 translate-middle-x border-radius-10 border-color-branco2" style={{ top: 73 }}>
              <div className="border rounded border-color-cinza1 bg-color-branco2 text-color-cinza5 p-2 font-size-12" style={{ whiteSpace: 'nowrap' }}>
                {label}
              </div>
            </div>
          )}
        </div>
      )
    }

    return (
      <div className="horizontal-timeline mb-3 p-5 bg-gray-03 bg-color-branco2" style={{ borderRadius: '12px' }}>
        <div className="d-flex align-items-center">
          {timelineSteps.map((step, index) => {
            const defaultLine = step.status === 'disabled' ? '#A8A8A8' : '#498EF2'
            const lineColor = resolveColor(step.corBorda, defaultLine)
            return (
              <React.Fragment key={index}>
                <StepCircle
                  icon={step.icon}
                  status={step.status}
                  overlay={step.overlay}
                  label={step.label}
                  index={index}
                  corBorda={step.corBorda}
                  corFundo={step.corFundo}
                  corIcone={step.corIcone}
                />
                {index < timelineSteps.length - 1 && (
                  <div className="flex-grow-1 position-relative" style={{ height: 8, backgroundColor: lineColor, zIndex: 1 }}></div>
                )}
              </React.Fragment>
            )
          })}
        </div>
      </div>
    )
  }

  const VerticalTimeline = () => {
    const selectedStep = timelineSteps[selectedStepIndex]
    const selectedLabel = selectedStep?.label
    const selectedIcon = selectedStep?.icon
    const selectedEtapaId = selectedStep?.id_etapa

    const selectedOcorrencias = selectedEtapaId && timelineEvents[selectedEtapaId]
      ? timelineEvents[selectedEtapaId]
      : {}

    const ocorrenciaKeys = Object.keys(selectedOcorrencias)

    const [openAccordions, setOpenAccordions] = useState(() => {
      const initial = {}
      ocorrenciaKeys.forEach(key => { initial[key] = true })
      return initial
    })

    if (!selectedStep || !selectedLabel || !selectedIcon) {
      return null
    }

    const toggleAccordion = (key) => {
      setOpenAccordions(prev => ({ ...prev, [key]: !prev[key] }))
    }

    return (
      <div className="vertical-timeline mb-1 p-4 bg-gray-03 bg-color-branco2" style={{ borderRadius: '12px' }}>
        {ocorrenciaKeys.map((ocorrenciaId, ocIdx) => {
          const events = selectedOcorrencias[ocorrenciaId]
          const isOpen = openAccordions[ocorrenciaId] !== false

          return (
            <div key={ocorrenciaId} className={`timeline-section ${ocIdx < ocorrenciaKeys.length - 1 ? 'mb-3' : ''} bg-transparent border-0 shadow-none`}>
              <div className="d-flex align-items-center justify-content-between rounded-pill mb-4 bg-color-azul410"
                   style={{ padding: '12px 20px' }}>
                <div className="d-flex align-items-center gap-2">
                  <span className="d-inline-flex align-items-center justify-content-center rounded-3" style={{ width: 32, height: 32 }}>
                    <i className={`${selectedIcon} text-color-azul2 font-size-22`}></i>
                  </span>
                  <span className="fw-bold text-color-azul2" style={{ fontSize: 20 }}>{selectedLabel}</span>
                </div>
                <div className="d-flex align-items-center gap-3">
                  <span className="fw-semibold text-color-azul2">{ocorrenciaId}</span>
                  <button className="btn p-0 border-0 bg-transparent"
                          onClick={() => toggleAccordion(ocorrenciaId)}
                          aria-label="Alternar">
                    <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} text-color-azul2`}></i>
                  </button>
                </div>
              </div>

              <Collapse isOpen={isOpen}>
                <div className="timeline-events ps-0 border-0 bg-transparent">
                  {events.map((event, index) => (
                    <div key={index} className="timeline-event d-flex pb-0 mb-0 pt-0 mt-0 border-0 bg-transparent">
                      <div className="timeline-marker d-flex flex-column align-items-center me-3" style={{ alignSelf: 'stretch' }}>
                        {(() => {
                          const defaultFundo = event.valueIcon?.includes('times') ? '#EC221F' : event.valueIcon?.includes('lock') ? '#9e9e9e' : '#498EF2'
                          const fundo = resolveColor(event.corFundo, defaultFundo)
                          const icone = resolveColor(event.corIcone, '#FFFFFF')
                          return (
                            <div className="timeline-dot rounded-circle d-flex align-items-center justify-content-center border-0 shadow-none flex-shrink-0"
                                 style={{ width: '34px', height: '34px', outline: 'none', backgroundColor: fundo }}>
                              <i className={event.valueIcon || 'fas fa-check'} style={{ fontSize: '16px', color: icone }}></i>
                            </div>
                          )
                        })()}
                        {index < events.length - 1 && (
                          <div className="timeline-line bg-light-blue-01 flex-grow-1"
                               style={{ width: '4px', flex: '1 1 auto' }}></div>
                        )}
                      </div>
                      <div className="timeline-content flex-grow-1 border-0 mb-3">
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <h5 className="mb-0 fw-bold text-color-azul1">{event.title}</h5>
                          {event.hasLink && event.link && (
                            <a
                              href={event.link}
                              target={event.redirectTarget || '_blank'}
                              rel="noopener noreferrer"
                              className="text-color-azul1 text-decoration-none"
                              style={{ fontSize: '18px' }}
                            >
                              <i className={event.redirectIcon || 'fas fa-external-link-alt'}></i>
                            </a>
                          )}
                        </div>
                        {event.agenda && Array.isArray(event.agenda) && event.agenda.length > 0 && (
                          <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                            {event.agenda.map((item, i) => (
                              <div key={i} className="d-inline-flex align-items-center gap-2 rounded-2 px-2 py-2" style={{ backgroundColor: '#f0f0f0' }}>
                                {item.icone && <i className={`${item.icone} text-color-azul2`} style={{ fontSize: '18px' }}></i>}
                                <span className="font-size-14 text-muted">{item.valor}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {event.value && (
                          <div className="d-inline-flex align-items-center gap-2 rounded px-3 py-2 mb-2 text-color-azul2" style={{ backgroundColor: '#F0F0F0' }}>
                            {event.valorIcone && <i className={`${event.valorIcone}`} style={{ fontSize: '14px' }}></i>}
                            <span className="small">{event.value}</span>
                          </div>
                        )}
                        {event.eventoTexto && (
                          <div className="text-muted small mb-1">
                            {event.eventoTexto}
                          </div>
                        )}
                        <div className="text-muted small">
                          {event.date}
                          {event.user && (
                            <span> por {event.user}</span>
                          )}
                        </div>
                        {event.ctaText && event.botaoLink && (
                          <div className="mt-2">
                            <a
                              href={event.botaoLink}
                              target={event.botaoTarget || '_blank'}
                              rel="noopener noreferrer"
                              className="btn btn-cinza1 border-1 px-3 border-color-cinza3 rounded-3 font-size-14 font-w-600 text-color-azul2 text-decoration-none"
                            >
                              {event.ctaText}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Collapse>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <React.Fragment>
      <Row>
        <Col lg={12} className="">
          <HorizontalTimeline />
        </Col>

        <Col lg={12} className="">
          <VerticalTimeline />
        </Col>
      </Row>
    </React.Fragment>
  )
}
